import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Video, Users, Clock, RefreshCw, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface LiveRoom {
  name: string;
  sid: string;
  numParticipants: number;
  creationTime: string;
  specialistName: string;
  caregiverName: string;
  callId: string;
  status: string;
  startedAt: string | null;
}

export function LiveSessionsManagement() {
  const [rooms, setRooms] = useState<LiveRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [terminatingRoom, setTerminatingRoom] = useState<string | null>(null);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-video-rooms', {
        body: { action: 'list' },
      });

      if (error) throw error;
      setRooms(data.rooms || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to fetch live sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchRooms, 30000);
    return () => clearInterval(interval);
  }, []);

  const terminateRoom = async (roomName: string) => {
    setTerminatingRoom(roomName);
    try {
      const { data, error } = await supabase.functions.invoke('manage-video-rooms', {
        body: { action: 'terminate', roomName },
      });

      if (error) throw error;
      
      if (data.success) {
        toast.success('Session terminated successfully');
        fetchRooms();
      } else {
        toast.error('Failed to terminate session');
      }
    } catch (error) {
      console.error('Error terminating room:', error);
      toast.error('Failed to terminate session');
    } finally {
      setTerminatingRoom(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Live Video Sessions</h2>
          <p className="text-muted-foreground">
            Monitor and manage active video calls
          </p>
        </div>
        <Button onClick={fetchRooms} disabled={loading} variant="outline" size="sm">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {rooms.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Video className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              No active video sessions at the moment
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {rooms.map((room) => (
            <Card key={room.name} className="border-primary/20">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Video className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <CardTitle className="text-base">
                        {room.specialistName} ↔ {room.caregiverName}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground font-mono">
                        {room.name}
                      </p>
                    </div>
                  </div>
                  <Badge variant="default" className="bg-green-500 hover:bg-green-500">
                    Live
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-4 w-4" />
                      <span>{room.numParticipants} participant(s)</span>
                    </div>
                    {room.startedAt && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4" />
                        <span>
                          Started {formatDistanceToNow(new Date(room.startedAt), { addSuffix: true })}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={terminatingRoom === room.name}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {terminatingRoom === room.name ? 'Terminating...' : 'Terminate'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Terminate Video Session?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will immediately end the video call between{' '}
                          <strong>{room.specialistName}</strong> and{' '}
                          <strong>{room.caregiverName}</strong>. Both participants will be disconnected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => terminateRoom(room.name)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Terminate Session
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
